/**
 * Mercado Livre (redesign admin, Fase 3): quadro Kanban e lista dos pedidos do
 * canal `marketplace`, sobre `/api/platform/orders` — a mesma fonte da tela de
 * Pedidos. Mudar a etapa de um pedido é feito no detalhe (`/pedidos/:id`),
 * pela máquina de estados única; o quadro só agrupa (ver `marketplace-board.ts`).
 *
 * Reconciliação Anúncios × ERP: depende do catálogo de anúncios do ML, que a
 * Plataforma ainda não recebe. Declarada como fonte ausente, nunca como zero.
 */
import { createElement as h, useState, type ReactElement } from "react";
import { Link, useParams } from "react-router-dom";
import { CircleDollarSign, Clock3, PackageX, ShoppingBag } from "lucide-react";
import { PeriodOrdersBoundary } from "../components/PeriodOrdersBoundary.js";
import { StatusPanel } from "../components/StatusPanel.js";
import { AWAITING_CONTRACT_LABEL } from "../dashboard/kpis.js";
import { formatBrl, formatCount, formatDateTime } from "../format.js";
import { isOrdersSourceAwaitingContract, type OrdersDataset } from "../orders/dataset.js";
import {
  marketplaceColumns,
  marketplaceOrders,
  marketplaceSummary
} from "../orders/marketplace-board.js";
import { ORDER_STATE_LABELS } from "../orders/state-machine.js";
import { salesChannelLabel } from "../orders/channels.js";
import type { PlatformCatalogProduct, PlatformUnifiedOrder } from "../api/types.js";
import { ListingReconciliationPanel } from "../components/ListingReconciliationPanel.js";
import type { MarketplaceListing } from "../contracts/platform-extras.js";
import { useCatalogDataset, useMarketplaceListings } from "../queries.js";
import { Card, CardHeader, KanbanBoard, PageHeader, Segmented, StatTile } from "../ui/index.js";

export type MercadoLivreMode = "quadro" | "lista";

export interface MercadoLivreViewProps {
  orders?: OrdersDataset;
  /** Relógio injetável: a janela de 48 h dos concluídos precisa ser determinística em teste. */
  now?: Date;
  initialMode?: MercadoLivreMode;
  /** C4: `undefined` = fonte não ligada; `null` = rota ausente; lista = anúncios importados. */
  listings?: MarketplaceListing[] | null;
  /** Produtos do catálogo para reconciliar; `null` = catálogo indisponível. */
  products?: PlatformCatalogProduct[] | null;
  /** Canal de venda do quadro (slug do Bling). Padrão: `mercado_livre`. Outros canais não têm a reconciliação de anúncios. */
  channel?: string;
  /** Barra de período/atualização já montada. */
  toolbar?: ReactElement;
}

/** Quantos cartões cada coluna desenha: com a carga real o ML tem centenas de pedidos em aberto. */
const CARDS_PER_COLUMN = 30;

function OrderCard({ order }: { order: PlatformUnifiedOrder }): ReactElement {
  return h(
    "article",
    { className: "flex flex-col gap-1 rounded-control border border-border bg-surface p-3 text-sm", "data-record-id": order.id },
    h(Link, { to: `/pedidos/${order.id}`, className: "font-bold text-brand-text" }, order.externalId || order.id),
    h("span", { className: "text-muted" }, order.customer?.name ?? "Cliente não informado"),
    order.blingSituation ? h("span", { className: "text-xs text-muted" }, `Bling: ${order.blingSituation}`) : null,
    h(
      "span",
      { className: "flex items-center justify-between gap-2" },
      h("span", { className: "font-extrabold tabular-nums text-ink" }, order.totalCents === null ? "—" : formatBrl(order.totalCents)),
      h("span", { className: "text-xs text-muted" }, formatDateTime(order.placedAt))
    )
  );
}

function OrderList({ orders }: { orders: PlatformUnifiedOrder[] }): ReactElement {
  return h(
    "div",
    { className: "overflow-x-auto" },
    h(
      "table",
      { className: "w-full text-left text-sm", "data-table": "mercado-livre" },
      h(
        "thead",
        null,
        h(
          "tr",
          { className: "text-xs uppercase text-muted" },
          ["Pedido", "Cliente", "Estado", "Total", "Data"].map((title) => h("th", { key: title, scope: "col", className: "px-2 py-2" }, title))
        )
      ),
      h(
        "tbody",
        null,
        orders.map((order) =>
          h(
            "tr",
            { key: order.id, "data-record-id": order.id, className: "border-t border-border" },
            h("td", { className: "px-2 py-2" }, h(Link, { to: `/pedidos/${order.id}`, className: "font-semibold text-brand-text" }, order.externalId || order.id)),
            h("td", { className: "px-2 py-2" }, order.customer?.name ?? "—"),
            h("td", { className: "px-2 py-2" }, ORDER_STATE_LABELS[order.state]),
            h("td", { className: "px-2 py-2 tabular-nums" }, order.totalCents === null ? "—" : formatBrl(order.totalCents)),
            h("td", { className: "px-2 py-2 text-muted" }, formatDateTime(order.placedAt))
          )
        )
      )
    )
  );
}

export function MercadoLivreView({ orders, now = new Date(), initialMode = "quadro", listings, products, channel = "mercado_livre", toolbar }: MercadoLivreViewProps): ReactElement {
  const [mode, setMode] = useState<MercadoLivreMode>(initialMode);
  const isMercadoLivre = channel === "mercado_livre";
  const channelName = salesChannelLabel(channel);
  const header = h(PageHeader, {
    title: channelName,
    description: `Pedidos do canal ${channelName} por etapa. Para mudar a etapa, abra o pedido.`
  });

  if (!orders || isOrdersSourceAwaitingContract(orders)) {
    return h(
      "div",
      { className: "page page--mercado-livre flex flex-col gap-6", "data-page": "mercado-livre" },
      header,
      h(StatusPanel, {
        kind: "awaiting_source",
        title: `Pedidos de ${channelName} ${AWAITING_CONTRACT_LABEL}`,
        detail: "A rota /api/platform/orders não respondeu neste ambiente."
      })
    );
  }

  const summary = marketplaceSummary(orders.orders, channel);
  const all = marketplaceOrders(orders.orders, channel);
  const columns = marketplaceColumns(orders.orders, now, channel);

  return h(
    "div",
    { className: "page page--mercado-livre flex flex-col gap-6", "data-page": "mercado-livre" },
    h(PageHeader, {
      title: channelName,
      description: `Pedidos do canal ${channelName} por etapa. Para mudar a etapa, abra o pedido.`,
      actions: h(Segmented<MercadoLivreMode>, {
        label: "Modo de visualização",
        value: mode,
        options: [
          { value: "quadro", label: "Quadro" },
          { value: "lista", label: "Lista" }
        ],
        onChange: setMode
      })
    }),
    toolbar ?? null,
    h(
      "section",
      { "aria-label": `Indicadores de ${channelName}`, className: "grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" },
      h(StatTile, { label: "Pedidos no canal", value: formatCount(summary.total), icon: ShoppingBag, "data-kpi": "ml-total" }),
      h(StatTile, { label: "Em andamento", value: formatCount(summary.inProgress), icon: Clock3, "data-kpi": "ml-andamento" }),
      h(StatTile, {
        label: "Receita do canal",
        value: formatBrl(summary.revenueCents),
        hint: "Pagos até entregues.",
        icon: CircleDollarSign,
        "data-kpi": "ml-receita"
      }),
      h(StatTile, {
        label: "Cancelados / devolvidos",
        value: formatCount(summary.undone),
        hint: "Fora do quadro.",
        icon: PackageX,
        "data-kpi": "ml-desfeitos"
      })
    ),
    all.length === 0
      ? h(StatusPanel, {
          kind: "empty",
          title: `Nenhum pedido de ${channelName} ainda`,
          detail: "Pedidos aparecem aqui quando o Bling os envia com este canal de venda."
        })
      : mode === "quadro"
        ? h(KanbanBoard, {
            label: `Pedidos de ${channelName} por etapa`,
            columns: columns.map((column) => ({
              id: column.id,
              title: column.orders.length > CARDS_PER_COLUMN ? `${column.title} (${formatCount(column.orders.length)})` : column.title,
              hint: column.orders.length > CARDS_PER_COLUMN ? `${column.hint} · mostrando os ${CARDS_PER_COLUMN} mais recentes` : column.hint,
              cards: column.orders.slice(0, CARDS_PER_COLUMN).map((order) => ({ id: order.id, node: h(OrderCard, { order }) }))
            }))
          })
        : h(Card, null, h(OrderList, { orders: [...all].sort((a, b) => b.placedAt.localeCompare(a.placedAt)).slice(0, 200) }), all.length > 200 ? h("p", { className: "m-0 mt-2 text-xs text-muted" }, `Mostrando os 200 mais recentes de ${formatCount(all.length)} pedidos. Veja todos em Pedidos, filtrando pelo canal.`) : null),
    !isMercadoLivre
      ? null
      : h(
      Card,
      { "data-panel": "reconciliacao" },
      h(CardHeader, { title: "Reconciliação Anúncios × ERP", description: "Anúncios do Mercado Livre contra produtos do ERP." }),
      // C4: `listings === undefined` = a view foi montada sem a fonte de anúncios (teste/SSR antigo) e
      // continua declarando a pendência; `null` = rota ausente; lista = reconciliação real.
      listings === undefined
        ? h(StatusPanel, {
            kind: "awaiting_source",
            title: `Catálogo de anúncios ${AWAITING_CONTRACT_LABEL}`,
            detail: "A Plataforma ainda não recebe os anúncios do Mercado Livre; sem eles não há como reconciliar."
          })
        : h(ListingReconciliationPanel, { listings, products: products ?? null })
    )
  );
}

/** Quadro de qualquer canal de venda do Bling (`/canais/:canal`): Shopee, Magalu, Amazon, TikTok, Shein… */
export function CanalVenda(): ReactElement {
  const { canal = "" } = useParams();
  return h(PeriodOrdersBoundary, {
    defaultPeriod: "mes",
    children: (orders: OrdersDataset, context) => h(MercadoLivreView, { orders, channel: canal, toolbar: context.toolbar })
  });
}

export function MercadoLivre(): ReactElement {
  const listings = useMarketplaceListings();
  const catalog = useCatalogDataset();
  return h(PeriodOrdersBoundary, {
    defaultPeriod: "mes",
    children: (orders: OrdersDataset, context) =>
      h(MercadoLivreView, {
        orders,
        toolbar: context.toolbar,
        // Enquanto carrega, nada de "aguardando contrato" falso: a lista chega em instantes.
        listings: listings.isPending ? undefined : listings.error ? null : (listings.data ?? null),
        // Catálogo indisponível (rota ausente) = sem produtos para comparar.
        products: catalog.data && catalog.data.sources.platformCatalog !== "awaiting_contract" ? catalog.data.products : null
      })
  });
}
